import * as React from "react";
import * as S from "@styled";
import Select from "shared/components/select/Select";
import { Link, useRouteMatch } from "react-router-dom";

import { connect } from "react-redux";
import { DispatchType, Utilities } from "../../shared/utilities";
import { addGlobalErrorHandlerElement } from "../actions/AddOrUpdateElement";
import { discardDraft } from "../actions/DiscardDraftAction";
import { exportSvg, exportXml } from "../actions/ImportExportActions";
import { removeGlobalErrorHandler } from "../actions/RemoveElement";
import { updateDescription } from "../actions/UpdateDescription";
import { updateName } from "../actions/UpdateName";
import { updateTargetFramework } from "../actions/UpdateTargetFramework";
import { updateMajorVersion, updateMinorVersion } from "../actions/UpdateVersion";
import { updateCancellationDisabled } from "../actions/UpdateCancellationDisabled";

import { Process } from "../model/Process";
import { ValidationError, ValidationErrorType } from "../model/ValidationData";

import { State } from "../model/State";

import { hasValidationError } from "./Helpers";

import { ImportBpmnModal } from "./Modals/ImportBpmnModal";
import { ChangeEvent } from "../../shared/model/Event";
import { Option } from "shared/model/Option";
import CustomToggle from "shared/components/CustomToggle";
import LabelWithTooltip from "shared/components/LabelWithTooltip";
import ProcessChangeLogModal from "./Modals/ProcessChangeLogModal";
import Time from "shared/Time";
import * as SidePanelActions from "../actions/SidePanelActions";
import { DocumentationLocationState } from "./Documentation/ProcessDocumentationView";

const versionDotStyles: React.CSSProperties = {
    height: "10px",
    margin: "0 10px"
};

export const SettingsEditor: React.FC<SettingsEditorProps> = (props) => {

    const [showChangeLogModal, setShowChangeLogModal] = React.useState<boolean>(false);
    const [showImportModal, setShowImportModal] = React.useState<boolean>(false);
    const { url } = useRouteMatch();
    const toDocumentation = url.endsWith('/')
        ? `${url}documentation`
        : `${url}/documentation`;

    const hideAllModals = () => {
        setShowChangeLogModal(false);
        setShowImportModal(false);
    }
    const handleImportBpmn = () => setShowImportModal(true);
    const handleClickShowChangeLog = () => setShowChangeLogModal(true);
    const handleClickSelectProcessErrorHandler = () => {

        props.addGlobalErrorHandler();
        props.onProcessErrorHandlerShowClick();
    }
    const handleClickRemoveErrorHandler = () => {
        if (props.isGlobalErrorHandlerPanelOpen) {
            props.closeSidePanel();
        }
        props.removeGlobalErrorHandler();
    }
    const handleChangeTargetFramework = (e: Option) => {
        props.onUpdateTargetFramework(e.value);
    }

    const processNameHasErrors = props?.validationErrors
        ? hasValidationError(props.validationErrors, ValidationErrorType.NoProcessName)
        : false;

    // cannot show change log for newly created process, or draft with no existing process
    // the ID (or parent process ID) is then 0 (falsy)
    const allowShowChangeLog = (props.process?.id ?? 0) > 0 &&
        (!props.process?.isDraft || props?.process.idOfBaseProcessForDraft !== undefined && props?.process.idOfBaseProcessForDraft > 0);

    const targetFrameworkOptions: Option[] = [
        {
            label: "NET Framework 4.7.1",
            value: "net471"
        }, {
            label: "NET Standard 2.0",
            value: "netstandard2.0"
        }, {
            label: "NET 8.0",
            value: "net8.0"
        }
    ];
    return (
        <S.ParameterFieldset>
            {props.process?.isSubprocess && (
                <S.FormGroup>
                    <h3>
                        {FRENDS.Localizations.Subprocess}
                    </h3>
                </S.FormGroup>
            )}

            {(props.process?.isDraft && !props?.disableEdit) && (
                <S.FormGroup>
                    <h3>
                        <span> {FRENDS.Localizations.Draft}
                            <span title={FRENDS.Localizations.Discard} onClick={props.discardDraft}> <i className="ci-trash_full pointer"></i> </span>
                        </span>
                    </h3>
                </S.FormGroup>
            )}

            <S.FormGroup variant={processNameHasErrors ? S.FormGroupVariant.error : S.FormGroupVariant.primary}>
                <S.Label>
                    {FRENDS.Localizations.Name}
                </S.Label>
                <S.Input
                    isFullWidth={true}
                    placeholder={FRENDS.Localizations.Name}
                    onChange={props.updateName}
                    disabled={props.disableEdit}
                    id="process-name-input"
                    defaultValue={props.process?.name}
                />
            </S.FormGroup>

            <S.FormGroup>
                <S.Label>
                    {FRENDS.Localizations.Version}
                </S.Label>
                <S.FlexContainer centerItems={true}>
                    <S.Input
                        disabled={props.disableEdit}
                        type="number"
                        min="0"
                        value={props.process?.majorVersion?.toString()}
                        onChange={props.updateMajorVersion}
                        id="process-major-version-input"
                        style={{ width: "70px" }}
                    />
                    <div style={versionDotStyles}><span>.</span></div>
                    <S.Input
                        disabled={props.disableEdit}
                        type="number"
                        min="0"
                        value={props.process?.minorVersion?.toString()}
                        onChange={props.updateMinorVersion}
                        id="process-minor-version-input"
                        style={{ width: "70px" }}
                    />
                    <div style={versionDotStyles}><span>.</span></div>
                    <p>{props.process?.version}</p>
                </S.FlexContainer>
            </S.FormGroup>
            <S.FormGroup>
                <S.Label>
                    {FRENDS.Localizations.LastModified}
                </S.Label>
                <p>{Time.toDetailedLocalTime(props.process?.modified)} - {props.process?.modifier}</p>
            </S.FormGroup>

            {
                props.process?.frendsVersion && (
                    <S.FormGroup>
                        <S.Label>
                            {FRENDS.Localizations.FrendsVersion}
                        </S.Label>
                        <p>{props.process.frendsVersion}</p>
                    </S.FormGroup>
                )
            }
            <S.FormGroup>
                <S.Label>
                    {FRENDS.Localizations.Description}
                </S.Label>
                <S.TextArea
                    rows={4}
                    disabled={props.disableEdit}
                    value={props.process?.description}
                    onChange={props.updateDescription}
                    id="process-description-input"
                >
                </S.TextArea>
            </S.FormGroup>
            <S.FormGroup>
                <S.Button as={Link} to={{ pathname: toDocumentation, state: { fromEditor: true } as DocumentationLocationState }}>
                    <S.ButtonIcon className="ci-file_blank_outline" />
                    Documentation
                </S.Button>
            </S.FormGroup>
            {
                props.disableEdit && !props.process?.hasGlobalErrorHandler ? null :
                    <S.FormGroup>
                        <S.Label>
                            {FRENDS.Localizations.ProcessErrorHandler}
                        </S.Label>
                        {props.process?.hasGlobalErrorHandler ?
                            <div>
                                <a className="pointer" onClick={props.onProcessErrorHandlerShowClick}>{props.processGlobalErrorHandlerName}  </a>
                                {!props.disableEdit && <i title={FRENDS.Localizations.Remove} className="ci-trash_full pointer" onClick={handleClickRemoveErrorHandler}></i>}
                            </div>
                            : <div>
                                <S.Button id="process-select-error-handler" onClick={handleClickSelectProcessErrorHandler}>
                                    {FRENDS.Localizations.Select} {FRENDS.Localizations.ProcessErrorHandler.toLowerCase()}
                                </S.Button>
                            </div>}
                    </S.FormGroup>
            }

            <S.FormGroup>
                <S.Label>
                    {FRENDS.Localizations.GraphControls}
                </S.Label>
                <S.ButtonGroup gap={12} $wrap={true}>
                    <S.Button id="process-import-bpmn" disabled={props.disableEdit} onClick={handleImportBpmn}>{FRENDS.Localizations.ImportBpmn}</S.Button>
                    <S.Button id="process-export-bpmn" disabled={props.disableEdit} onClick={props.exportXml}>{FRENDS.Localizations.ExportBpmn}</S.Button>
                    <S.Button id="process-export-picture" disabled={props.disableEdit} onClick={props.exportPicture}>{FRENDS.Localizations.SaveAsPicture}</S.Button>
                </S.ButtonGroup>
            </S.FormGroup>

            {allowShowChangeLog && (
                <S.FormGroup>
                    <S.Label>
                        {Utilities.humanizeLabel(FRENDS.Localizations.ChangeLog)}
                    </S.Label>
                    <S.ButtonGroup gap={12}>
                        <S.Button id="process-show-changelog" onClick={handleClickShowChangeLog}>{FRENDS.Localizations.ShowChangeLog}</S.Button>
                        <S.Button id="process-show-diff" as={Link} to={{ pathname: props.path, search: "?diff=true" }}>
                            {FRENDS.Localizations.Diff}
                        </S.Button>
                    </S.ButtonGroup>
                </S.FormGroup>
            )}

            {
                showChangeLogModal && (
                    <ProcessChangeLogModal
                        key="changeLogModal"
                        processGuid={props.process?.uniqueIdentifier}
                        isSubProcess={props.process?.isSubprocess}
                        processId={props.process?.id}
                        processName={props.process?.name}
                        onHide={hideAllModals}
                    />
                )
            }

            {showImportModal ? <ImportBpmnModal key="importBpmnModal" onHide={hideAllModals} /> : null}

            <S.FormGroup>
                <S.Label>
                    {FRENDS.Localizations.TargetFramework}
                </S.Label>
                <Select
                    id="select-target-framework"
                    value={Utilities.valueFromId(targetFrameworkOptions, props.process?.targetFramework)}
                    options={targetFrameworkOptions}
                    placeholder={"Select target framework"}
                    onChange={handleChangeTargetFramework}
                    isDisabled={props.disableEdit}
                    menuPlacement="top"
                />
            </S.FormGroup>

            <S.FormGroup $inline={true}>
                <LabelWithTooltip
                    label="Disable caller cancellation"
                    description="If enabled, the Process will continue running regardless of possible cancellation from caller or trigger, i.e. cancelled HTTP-request."
                />

                {props.process &&
                    <CustomToggle
                        checked={props.process?.cancellationDisabled}
                        onChange={props.onChangeCancellationDisabled}
                        disabled={props.disableEdit}
                    />
                }
            </S.FormGroup>
        </S.ParameterFieldset>
    )
}

interface SettingsEditorOwnProps {
    disableEdit: boolean | undefined;
    process: Process | undefined;
    processGlobalErrorHandlerName: string | undefined;
    validationErrors: ValidationError[] | undefined;
    onProcessErrorHandlerShowClick: () => void;
}
interface SettingsDispatchProps {
    updateDescription: (changeEvent: any) => void;
    discardDraft: () => void;
    updateName: (changeEvent: ChangeEvent<HTMLInputElement>) => void;
    updateMajorVersion: (changeEvent: ChangeEvent<HTMLInputElement>) => void;
    updateMinorVersion: (changeEvent: ChangeEvent<HTMLInputElement>) => void;
    exportXml: () => void;
    closeSidePanel: () => void;
    exportPicture: () => void;
    addGlobalErrorHandler: () => void;
    removeGlobalErrorHandler: () => void;
    onUpdateTargetFramework: (selectedFramework: string) => void;
    onChangeCancellationDisabled: (value: boolean) => void;
}

interface SettingsEditorStateProps {
    path: string;
    isGlobalErrorHandlerPanelOpen: boolean
}
interface SettingsEditorState {
    showChangeLogModal: boolean;
    showImportModal: boolean;
}

type SettingsEditorProps = SettingsEditorOwnProps & SettingsEditorState & SettingsEditorStateProps & SettingsDispatchProps;

function mapStateToProps(state: State, ownProps: SettingsEditorOwnProps): SettingsEditorStateProps {
    const isGlobalErrorHandlerPanelOpen = state.uiState.panelSettings.elementPanel.selectedElementId === "globalErrorHandler";
    return {
        path: state.router.location.pathname,
        isGlobalErrorHandlerPanelOpen: isGlobalErrorHandlerPanelOpen
    };
}

const mapDispatchToProps = (dispatch: DispatchType<State>, settings: SettingsEditorOwnProps): SettingsDispatchProps => {
    return {
        updateDescription: (changeEvent) => dispatch(updateDescription(changeEvent.target.value)),
        discardDraft: () => dispatch(discardDraft(settings.process?.id, true, settings.process?.isSubprocess)), // True flag is to redirect after discard action
        updateName: (changeEvent) => dispatch(updateName(changeEvent.target.value)),
        updateMajorVersion: (changeEvent) => dispatch(updateMajorVersion(parseFloat(changeEvent.target.value))),
        updateMinorVersion: (changeEvent) => dispatch(updateMinorVersion(parseFloat(changeEvent.target.value))),
        exportXml: () => dispatch(exportXml()),
        closeSidePanel: () => dispatch(SidePanelActions.closeElementPanel()),
        exportPicture: () => dispatch(exportSvg()),
        addGlobalErrorHandler: () => dispatch(addGlobalErrorHandlerElement()),
        removeGlobalErrorHandler: () => dispatch(removeGlobalErrorHandler()),
        onUpdateTargetFramework: (selectedFramework: string) => dispatch(updateTargetFramework(selectedFramework)),
        onChangeCancellationDisabled: (value: boolean) => dispatch(updateCancellationDisabled(value)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(SettingsEditor) as React.ComponentType<SettingsEditorOwnProps>;
